/**
 * Home's brief: a sentence or two under the greeting naming only
 * what needs a person's hand now. Overdue findings (else the ones assigned
 * to you), failed workflows, the first review due within a week, and updates
 * to assess. Each sentence leads with a link to the thing it names.
 *
 * @module components/home/homeBrief.logic
 */
import type { WorkflowSummary } from "@amoreg/contracts";

import type { FindingItem } from "../regulation/finding/findingItem";
import { daysUntil, relativeDue } from "./HomeAgenda.logic";
import { isFindingOverdue, type HomeGlance } from "./homeGlance.logic";
import type { HomeComingUpItem } from "./homeLogic";

export const BRIEF_DUE_WINDOW_DAYS = 7;

/** Where a sentence's link goes: one thing opens that thing, several open their list. */
export type BriefTarget =
  | { readonly kind: "finding"; readonly findingId: string }
  | { readonly kind: "findings" }
  | { readonly kind: "workflow"; readonly workflowId: string }
  | { readonly kind: "workflows" }
  | { readonly kind: "policy"; readonly policyId: string }
  | { readonly kind: "regulatory" };

/**
 * A sentence's words, in the runs they are written in. Each run is its own text node, which shapes the
 * line a fraction of a pixel differently from one joined string.
 */
export type BriefWords = ReadonlyArray<string>;

export interface BriefSentence {
  readonly key: string;
  readonly link: BriefWords;
  /** The words after the link, with the full stop. */
  readonly rest: BriefWords;
  readonly target: BriefTarget;
  /** The link draws the app's focus ring; the others keep the browser's outline, as the sketch did. */
  readonly ringFocus: boolean;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : `${n} ${many}`);

const findingsTarget = (items: ReadonlyArray<FindingItem>): BriefTarget =>
  items.length === 1 ? { kind: "finding", findingId: items[0]!.id } : { kind: "findings" };

const workflowsTarget = (failing: ReadonlyArray<WorkflowSummary>): BriefTarget =>
  failing.length === 1 ? { kind: "workflow", workflowId: failing[0]!.id } : { kind: "workflows" };

export function briefSentences(input: {
  readonly glance: Pick<HomeGlance, "mine" | "failing" | "toReview">;
  readonly comingUp: ReadonlyArray<HomeComingUpItem>;
  readonly now: number;
}): ReadonlyArray<BriefSentence> {
  const { glance, comingUp, now } = input;
  const sentences: BriefSentence[] = [];
  const overdue = glance.mine.filter((item) => isFindingOverdue(item, now));
  if (overdue.length > 0) {
    sentences.push({
      key: "overdue",
      link: [plural(overdue.length, "One finding", "findings"), " ", overdue.length === 1 ? "is" : "are", " overdue"],
      rest: ["."],
      target: findingsTarget(overdue),
      ringFocus: false,
    });
  } else if (glance.mine.length > 0) {
    sentences.push({
      key: "mine",
      link: [plural(glance.mine.length, "One finding", "findings"), " ", glance.mine.length === 1 ? "is" : "are", " assigned to you"],
      rest: ["."],
      target: findingsTarget(glance.mine),
      ringFocus: false,
    });
  }
  if (glance.failing.length > 0) {
    const only = glance.failing.length === 1 ? glance.failing[0]! : null;
    sentences.push({
      key: "failed",
      link: only ? [only.name, " failed"] : [String(glance.failing.length), " workflows failed"],
      rest: ["."],
      target: workflowsTarget(glance.failing),
      ringFocus: only !== null,
    });
  }
  // Reviews only: a publication's date is not a review falling due, and must not hide one that is.
  const due = comingUp.find((item) => item.kind === "review" && daysUntil(Date.parse(item.at), now) <= BRIEF_DUE_WINDOW_DAYS);
  if (due) {
    const at = Date.parse(due.at);
    sentences.push({
      key: `due:${due.id}`,
      link: [due.context ? `${due.title} for ${due.context}` : due.title],
      rest: [" ", daysUntil(at, now) < 0 ? `is ${relativeDue(at, now)}` : `is due ${relativeDue(at, now)}`, "."],
      target: due.policyId ? { kind: "policy", policyId: due.policyId } : { kind: "regulatory" },
      ringFocus: true,
    });
  }
  if (glance.toReview.length > 0) {
    sentences.push({
      key: "updates",
      link: [plural(glance.toReview.length, "One regulatory update", "regulatory updates")],
      rest: [" ", glance.toReview.length === 1 ? "needs" : "need", " review."],
      target: { kind: "regulatory" },
      ringFocus: false,
    });
  }
  return sentences;
}
